// One Over Time chart on Reporting (owner, 2026-11-04): reads
// `GET /analytics/over-time` for the chart's grain under the Filters' criteria
// and draws the chart's measure as a line (`docs/api.md` §16.10).
//
// `onRows` hands Download the rows it writes (`overTimeRows`): the screen keeps
// them for the chart on screen, as it keeps a bar chart's. They are `null`
// while the read is in flight or has failed.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Caption1, makeStyles, tokens } from '@fluentui/react-components'
import { errorMessage } from '../api/errors'
import type { AnalyticsOverTime } from '../api/types'
import { toAnalyticsParams, type Criteria } from '../lib/criteria'
import { loadAnalyticsOverTime } from '../lib/data'
import {
  OVER_TIME_COLOR,
  overTimeAxis,
  overTimeChartById,
  overTimePoints,
  overTimeRows,
} from '../lib/overTimeCharts'
import type { BarRowData } from './DashboardCharts'
import { OverTimeChart } from './OverTimeChart'
import { EmptyQueue, ErrorBanner, Loading } from './States'

const useStyles = makeStyles({
  hint: { display: 'block', marginTop: '8px', color: tokens.colorNeutralForeground3 },
})

const PERIOD_NOUN = { month: 'months', week: 'weeks', day: 'days', cycle: 'payment cycles' } as const

export function OverTimeView({
  chartId,
  criteria,
  refreshNonce,
  onRows,
}: {
  chartId: string
  criteria: Criteria
  refreshNonce: number
  /** Download's rows for this chart, oldest period first, and whether the range was cut. */
  onRows?: (rows: BarRowData[] | null, truncated: boolean) => void
}) {
  const s = useStyles()
  const chart = overTimeChartById(chartId)
  const grain = chart?.grain
  const [data, setData] = useState<AnalyticsOverTime | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)
  const retry = useCallback(() => setAttempt((n) => n + 1), [])

  // The criteria object may be rebuilt on every render; its content is the key.
  const paramsKey = JSON.stringify(toAnalyticsParams(criteria))

  useEffect(() => {
    if (grain === undefined) return
    let cancelled = false
    setLoading(true)
    setError(null)
    loadAnalyticsOverTime(grain, JSON.parse(paramsKey) as ReturnType<typeof toAnalyticsParams>)
      .then((answer) => {
        if (cancelled) return
        setData(answer)
        setLoading(false)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setData(null)
        setError(errorMessage(e))
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [grain, paramsKey, refreshNonce, attempt])

  // The answer for THIS chart's grain: after a switch from a month chart to a
  // week chart, the month answer is not drawn while the week one loads.
  const answer = chart !== undefined && !loading && data !== null && data.grain === chart.grain ? data : null
  const rows = useMemo(() => (chart === undefined || answer === null ? null : overTimeRows(chart, answer)), [chart, answer])
  const truncated = answer?.truncated === true

  // The latest callback, so a parent passing an inline function does not re-run the effect below.
  const onRowsRef = useRef(onRows)
  useLayoutEffect(() => {
    onRowsRef.current = onRows
  })
  useEffect(() => {
    onRowsRef.current?.(rows, truncated)
  }, [rows, truncated])

  if (chart === undefined) return null
  if (error !== null) return <ErrorBanner message={error} onRetry={retry} />
  if (answer === null) return <Loading label="Loading the chart…" />

  const points = overTimePoints(chart, answer)
  // Empty when nothing was uploaded or exported in the range: a run of zeros
  // with invoices behind it (no flags, say) is an answer and is drawn.
  if (points.every((p) => p.value === null) || answer.periods.every((p) => p.invoice_count === 0 && p.export_count === 0)) {
    return <EmptyQueue text="Nothing to show for these filters." />
  }
  const axis = overTimeAxis(chart)
  return (
    <>
      <OverTimeChart
        points={points}
        color={OVER_TIME_COLOR}
        axisFormat={axis.format}
        wholeOnly={axis.wholeOnly}
        ariaLabel={`${chart.title}: ${points.map((p) => `${p.label} ${p.display}`).join('; ')}`}
      />
      {truncated && (
        <Caption1 className={s.hint}>
          Showing the most recent {answer.periods.length} {PERIOD_NOUN[chart.grain]}.
        </Caption1>
      )}
    </>
  )
}
